/**
 * Фото осмотра с веба (founder 16.08.2026 — «делаем а сначала»): консультант
 * может сфотографировать находку с телефона (файл из галереи ИЛИ снимок с
 * камеры браузера через `capture="environment"`) или с десктопа (обычный
 * файл). Мобильное приложение сжимает фото на клиенте перед отправкой —
 * веб делает то же самое здесь, потому что presign выдаётся под лимит
 * backend `INSPECTION_PHOTO_MAX_BYTES` (512 КБ, `rasters/inspection_photos.py`),
 * а необработанное фото с камеры телефона обычно 2-8 МБ.
 *
 * Поток: File → downscale+reencode (canvas) → presign → PUT в S3 напрямую.
 */
import { api } from '@/lib/api';

const MAX_BYTES = 480 * 1024; // запас против серверного лимита 512 КБ
const MAX_DIMENSION = 1600;

export interface InspectionPhotoRef {
  id: string;
  url: string;
  object_key: string;
  taken_at: string;
}

/** Сжимает изображение до ориентировочного лимита. Best-effort: если canvas
 * недоступен (крайне редкий браузер) — возвращает исходный файл как есть,
 * а presign/PUT сами сообщат об ошибке размера, если она случится. */
export async function compressInspectionPhoto(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    for (const quality of [0.82, 0.65, 0.5, 0.35]) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality),
      );
      if (blob && blob.size <= MAX_BYTES) return blob;
      if (blob && quality === 0.35) return blob; // отдаём наименьшее, что получилось
    }
    return file;
  } catch {
    return file; // fallback — пусть решает backend (413 обработается в UI)
  }
}

interface PresignResponse {
  upload_url: string;
  object_key: string;
  public_url: string;
  content_type: string;
  expires_at: string;
}

/**
 * Presign + прямой PUT в S3. БЕЗ Bearer/cookie на PUT — presigned-подпись
 * уже в query-string, добавление Authorization ломает проверку подписи
 * (тот же нюанс, что у мобильного `uploadClient`, см. InspectionsApi.kt).
 */
export async function uploadInspectionPhoto(
  inspectionId: string,
  photoId: string,
  file: Blob,
  contentType: string,
): Promise<InspectionPhotoRef> {
  const presign = await api.post<PresignResponse>('/api/v1/inspections/photos/presign', {
    inspection_id: inspectionId,
    photo_id: photoId,
    content_type: contentType,
    bytes: file.size,
  });

  const put = await fetch(presign.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!put.ok) {
    throw new Error(`Не удалось загрузить фото (S3 ${put.status})`);
  }

  return {
    id: photoId,
    url: presign.public_url,
    object_key: presign.object_key,
    taken_at: new Date().toISOString(),
  };
}
